
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminSidebar } from '@/components/AdminSidebar';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AreaChart, BarChart, LineChart, ResponsiveContainer, Area, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Calendar, Download, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageTransition from '@/components/PageTransition';
import { toast } from 'sonner';

// Sample data for charts
const pageViewData = [
  { name: '1 Mar', Homepage: 1200, Features: 800, Pricing: 600, Blog: 400 },
  { name: '8 Mar', Homepage: 1400, Features: 850, Pricing: 700, Blog: 500 },
  { name: '15 Mar', Homepage: 1300, Features: 900, Pricing: 650, Blog: 600 },
  { name: '22 Mar', Homepage: 1500, Features: 950, Pricing: 750, Blog: 650 },
  { name: '29 Mar', Homepage: 1600, Features: 1000, Pricing: 800, Blog: 700 },
  { name: '5 Apr', Homepage: 1800, Features: 1200, Pricing: 850, Blog: 750 },
  { name: '12 Apr', Homepage: 2000, Features: 1300, Pricing: 900, Blog: 800 },
];

const deviceData = [
  { name: 'Mobile', value: 58 },
  { name: 'Desktop', value: 36 },
  { name: 'Tablet', value: 6 },
];

const conversionData = [
  { name: '1 Mar', 'Conversion Rate': 1.8 },
  { name: '8 Mar', 'Conversion Rate': 2.3 },
  { name: '15 Mar', 'Conversion Rate': 2.1 },
  { name: '22 Mar', 'Conversion Rate': 2.6 },
  { name: '29 Mar', 'Conversion Rate': 3.1 },
  { name: '5 Apr', 'Conversion Rate': 3.5 },
  { name: '12 Apr', 'Conversion Rate': 4.2 },
];

const referralData = [
  { name: 'Google', value: 42 },
  { name: 'Direct', value: 28 },
  { name: 'Social', value: 18 },
  { name: 'Email', value: 8 },
  { name: 'Other', value: 4 },
];

const Metrics = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if the user is authenticated as admin
    const authData = localStorage.getItem('adminAuth');
    if (!authData || !JSON.parse(authData).authenticated) {
      toast.error('You must be logged in as admin to view this page');
      navigate('/login', { state: { isAdmin: true } });
    }
  }, [navigate]);

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      
      <div className="flex-1 p-6 overflow-y-auto">
        <PageTransition>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Site Metrics</h1>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm">
                <Calendar className="mr-2 h-4 w-4" />
                Last 30 Days
              </Button>
              <Button variant="outline" size="sm">
                <Filter className="mr-2 h-4 w-4" />
                Filter
              </Button>
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </div>
          </div>
          
          <Separator className="mb-6" />
          
          {/* Analytics Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="acquisition">Acquisition</TabsTrigger>
              <TabsTrigger value="behavior">Behavior</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview">
              {/* Stats Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardHeader className="p-4 pb-2">
                    <CardDescription>Total Users</CardDescription>
                    <CardTitle className="text-2xl">28,456</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <p className="text-xs text-muted-foreground">+12.3% from last month</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="p-4 pb-2">
                    <CardDescription>Page Views</CardDescription>
                    <CardTitle className="text-2xl">86,927</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <p className="text-xs text-muted-foreground">+8.7% from last month</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="p-4 pb-2">
                    <CardDescription>Avg. Session</CardDescription>
                    <CardTitle className="text-2xl">3m 24s</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <p className="text-xs text-muted-foreground">+14.1% from last month</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="p-4 pb-2">
                    <CardDescription>Bounce Rate</CardDescription>
                    <CardTitle className="text-2xl">28.4%</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 pt-0">
                    <p className="text-xs text-green-500">-2.7% from last month</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Page Views Chart */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Page Views by Page</CardTitle>
                  <CardDescription>Traffic trends across main pages</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={pageViewData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="Homepage" stackId="1" stroke="#33C3F0" fill="#33C3F0" fillOpacity={0.8} />
                        <Area type="monotone" dataKey="Features" stackId="1" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                        <Area type="monotone" dataKey="Pricing" stackId="1" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                        <Area type="monotone" dataKey="Blog" stackId="1" stroke="#ffc658" fill="#ffc658" fillOpacity={0.6} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Device Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Device Distribution</CardTitle>
                  <CardDescription>Users by device type</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={deviceData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} />
                        <YAxis type="category" dataKey="name" />
                        <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                        <Legend />
                        <Bar dataKey="value" fill="#33C3F0" name="Percentage" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="acquisition">
              {/* Referral Distribution */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Traffic Sources</CardTitle>
                  <CardDescription>Where users are coming from</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={referralData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                        <Legend />
                        <Bar dataKey="value" fill="#33C3F0" name="Percentage" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Conversion Rate */}
              <Card>
                <CardHeader>
                  <CardTitle>Conversion Rate</CardTitle>
                  <CardDescription>Rate of visitors completing desired actions</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={conversionData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value) => [`${value}%`, 'Conversion Rate']} />
                        <Legend />
                        <Line type="monotone" dataKey="Conversion Rate" stroke="#33C3F0" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="behavior">
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Most Viewed Pages</CardTitle>
                    <CardDescription>Top performing pages by views</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Homepage</span>
                        <span>28,456 views</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Pricing</span>
                        <span>14,328 views</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Features</span>
                        <span>12,654 views</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Blog: Emergency Preparedness</span>
                        <span>8,943 views</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="font-medium">About</span>
                        <span>5,219 views</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Top Exit Pages</CardTitle>
                    <CardDescription>Pages where users most commonly leave the site</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Pricing</span>
                        <span>28.3%</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Contact</span>
                        <span>18.7%</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Blog</span>
                        <span>15.2%</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Features</span>
                        <span>12.5%</span>
                      </div>
                      <Separator />
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Homepage</span>
                        <span>8.9%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
          
        </PageTransition>
      </div>
    </div>
  );
};

export default Metrics;
